import React, { useState, useEffect } from 'react';
import { Smartphone, Sparkles, RefreshCw, LogOut, CheckCircle, Lock } from 'lucide-react';
import { useConfig } from '../hooks/useConfig';

const WA_PROVISION_WEBHOOK = "https://flow.seryiads.com/webhook/02c73d5f-816f-475a-9696-6a40b53c97d8";

export default function WhatsAppProvisioningV2({ activeBusiness, isSuperAdmin, isTrialAccount, isDemoAccount }) {
  const { updateActiveBusinessField, showNotification } = useConfig();
  
  const [waStatus, setWaStatus] = useState('disconnected');
  const [qrCode, setQrCode] = useState('');
  const [pollingInterval, setPollingInterval] = useState(null);
  const [activeInstanceName, setActiveInstanceName] = useState(null);

  useEffect(() => {
    if (activeBusiness?.instance_name) {
      setWaStatus('connected');
    } else {
      setWaStatus('disconnected');
      setQrCode('');
    }
  }, [activeBusiness?.id, activeBusiness?.instance_name]);

  useEffect(() => {
    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [pollingInterval]);

  const handleConnectWhatsApp = async () => {
    if (!activeBusiness || !activeBusiness.id) {
      showNotification("Selecciona un negocio primero.", "error");
      return;
    }

    setWaStatus('loading');
    setQrCode('');

    try {
      const payload = {
        action: 'create_instance',
        business_id: activeBusiness.id,
        instance_name: activeBusiness.id + 'inst', // <-- ENVIANDO EL NOMBRE DE LA INSTANCIA
        business_name: activeBusiness.nombre_negocio,
        webhook_n8n: activeBusiness.webhook_n8n || "https://flow.seryiads.com/webhook/keylacrm-sync"
      };

      const response = await fetch(WA_PROVISION_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.status === 'connected' || data.connected || data.state === 'open' || data.message === 'connected' || (response.ok && !data.qrcode && !data.qr && !data.base64 && !data.error && data.status !== 'error')) {
        setWaStatus('connected');
        showNotification("WhatsApp ya está conectado y activo para este negocio.", "success");
        updateActiveBusinessField('instance_name', data.instance?.instanceName || payload.instance_name || (activeBusiness.id + 'inst'));
      } else if (data.status === 'qrcode' || data.qrcode || data.qr || data.base64) {
        let rawQr = data.qrcode || data.qr || data.base64 || '';
        if (rawQr && !rawQr.startsWith('data:')) rawQr = "data:image/png;base64," + rawQr;
        setQrCode(rawQr);
        setWaStatus('qr_ready');
        showNotification("Código QR generado. Escanéalo en tu celular.");
        // Usar el instanceName que n8n/Evolution API realmente creó
        const resolvedName = data.instance_name || data.instance?.instanceName || data.instanceName || (activeBusiness.id + 'inst');
        setActiveInstanceName(resolvedName);
        startPolling(resolvedName);
      } else {
        setWaStatus('disconnected');
        showNotification("No se pudo obtener el QR. Intenta de nuevo.", "error");
      }
    } catch (error) {
      console.error(error);
      setWaStatus('disconnected');
      showNotification("Error de red conectando al servidor de WhatsApp.", "error");
    }
  };

  const startPolling = (instanceName) => {
    if (pollingInterval) clearInterval(pollingInterval);

    const interval = setInterval(async () => {
      try {
        const response = await fetch(WA_PROVISION_WEBHOOK, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'check_status', instance_name: instanceName })
        });
        const data = await response.json();
        
        const isConnected = data.state === 'open' || data.status === 'connected' || 
                           data.instance?.state === 'open' || data.connectionStatus === 'open';
        const isDisconnected = data.state === 'close' || data.status === 'disconnected' || 
                              data.state === 'refused' || data.status === 'error';

        if (isConnected) {
          clearInterval(interval);
          setPollingInterval(null);
          setWaStatus('connected');
          updateActiveBusinessField('instance_name', instanceName);
          showNotification("¡WhatsApp conectado exitosamente!", "success");
        } else if (isDisconnected) {
          clearInterval(interval);
          setPollingInterval(null);
          setWaStatus('disconnected');
          showNotification("La conexión fue rechazada o expiró.", "error");
        }
        // Si sigue en 'connecting', simplemente esperamos el siguiente ciclo
      } catch (err) {
        console.warn("Error en polling de WhatsApp:", err);
        // Si la instancia no existe (404), detenemos el polling
        if (err?.message?.includes('404') || err?.status === 404) {
          clearInterval(interval);
          setPollingInterval(null);
          setWaStatus('disconnected');
          showNotification("La instancia de WhatsApp no fue encontrada. Intenta vincular de nuevo.", "error");
        }
      }
    }, 5000);

    setPollingInterval(interval);
  };

  const handleDisconnectWhatsApp = async () => {
    if (isDemoAccount) {
      showNotification("Por seguridad, la desvinculación de WhatsApp está bloqueada en cuentas Demo.", "error");
      return;
    }

    setWaStatus('loading');
    const instanceName = activeBusiness.instance_name;

    try {
      await fetch(WA_PROVISION_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete_instance', instance_name: instanceName })
      });
    } catch (err) {
      console.warn("Error enviando desconexión:", err);
    }

    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    setWaStatus('disconnected');
    setQrCode('');
    updateActiveBusinessField('instance_name', '');
    updateActiveBusinessField('instance_token', '');
    showNotification("Instancia de WhatsApp desconectada con éxito.", "warning");
  };

  if (!activeBusiness) return null;

  if (!isSuperAdmin && (isTrialAccount || isDemoAccount || !activeBusiness.activo || activeBusiness.status === 'inactivo' || activeBusiness.status === 'pendiente_pago')) {
    return (
      <div className="bg-gradient-to-r from-orange-500/20 to-red-600/20 border border-orange-500/30 p-6 rounded-3xl mt-6 flex flex-col md:flex-row items-center gap-6 shadow-xl">
        <div className="bg-orange-500/10 p-4 rounded-full text-orange-400 shrink-0 hidden md:block">
          <Lock size={32} />
        </div>
        <div className="flex-1 text-center md:text-left space-y-2">
          <h4 className="font-black text-lg text-seryi-heading">🔒 Conexión de WhatsApp Restringida</h4>
          <p className="text-xs text-seryi-text leading-relaxed">
            Tu cuenta está inactiva, pendiente de pago o en un plan gratuito que no incluye automatización con WhatsApp Evolution API.
          </p>
        </div>
        <button 
          onClick={() => window.open('/planes', '_blank')}
          className="shrink-0 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-black py-3 px-6 rounded-xl text-xs transition-all shadow-lg shadow-orange-500/30 w-full md:w-auto text-center cursor-pointer"
        >
          ⭐ Actualizar Plan Ahora
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-slate-900 via-slate-900 to-indigo-950/40 border border-seryi-bg-light p-6 rounded-3xl space-y-6 mt-6">
      <div className="flex items-center justify-between border-b border-seryi-bg-light/80 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-seryi-green/10 border border-indigo-500/20 rounded-xl flex items-center justify-center">
            <Smartphone className="text-seryi-green" size={18} />
          </div>
          <div>
            <h3 className="font-bold text-sm text-seryi-heading">Agregar Trabajador 24/7 (WhatsApp)</h3>
            <p className="text-xs text-seryi-text">Vincula tu número para que tu asistente virtual trabaje por ti 24/7.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
        <div className="md:col-span-3 space-y-3">
          <h4 className="text-xs font-bold text-seryi-heading uppercase tracking-wide">Estado</h4>
          <p className="text-xs text-seryi-text leading-relaxed">
            {waStatus === 'connected' 
              ? "El asistente ya está conectado y operando tu WhatsApp correctamente."
              : waStatus === 'qr_ready'
              ? "El código de conexión QR ha sido generado. Abre WhatsApp en tu celular > Dispositivos vinculados > Vincular un dispositivo y apunta tu cámara hacia la pantalla."
              : waStatus === 'loading'
              ? "Estableciendo conexión segura con el aprovisionador de Evolution API. Por favor espera unos segundos..."
              : "Tu asistente de IA no está activa actualmente. Para iniciar la automatización de WhatsApp, haz clic en el botón para generar tu instancia e iniciar el lector de código QR."}
          </p>

          <div className="flex items-center gap-4 pt-2">
            {waStatus === 'disconnected' && (
              <button
                onClick={handleConnectWhatsApp}
                className="bg-seryi-green text-seryi-bg-dark hover:bg-seryi-green/80 font-bold py-2.5 px-5 rounded-xl text-xs flex items-center gap-2 transition-all shadow-md hover:scale-[1.02] active:scale-95"
              >
                <Sparkles size={14} /> Vincular mi WhatsApp
              </button>
            )}

            {waStatus === 'qr_ready' && (
              <div className="flex gap-2">
                <button
                  onClick={handleConnectWhatsApp}
                  className="bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 font-bold py-2.5 px-5 rounded-xl text-xs flex items-center gap-2 transition-all border border-indigo-500/20"
                >
                  <RefreshCw size={14} /> Regenerar QR
                </button>
                <button
                  onClick={() => {
                    setWaStatus('disconnected');
                    setQrCode('');
                    if (pollingInterval) clearInterval(pollingInterval);
                  }}
                  className="bg-rose-500/10 text-rose-500 hover:bg-rose-500/20 font-bold py-2.5 px-5 rounded-xl text-xs flex items-center gap-2 transition-all"
                >
                  Cancelar
                </button>
              </div>
            )}

            {waStatus === 'connected' && (
              <button
                onClick={handleDisconnectWhatsApp}
                className="bg-rose-500/10 text-rose-500 hover:bg-rose-500/20 font-bold py-2.5 px-5 rounded-xl text-xs flex items-center gap-2 transition-all border border-rose-500/20 shadow-md"
              >
                <LogOut size={14} /> Desvincular WhatsApp
              </button>
            )}
          </div>
        </div>

        <div className="md:col-span-2 flex justify-center md:justify-end">
          <div className="w-48 h-48 bg-slate-900 border-2 border-dashed border-indigo-500/30 rounded-3xl flex flex-col items-center justify-center p-3 relative overflow-hidden shadow-2xl">
            {waStatus === 'loading' ? (
              <div className="flex flex-col items-center text-indigo-400 gap-3">
                <RefreshCw size={24} className="animate-spin" />
                <span className="text-[10px] font-bold text-center">Conectando...</span>
              </div>
            ) : waStatus === 'qr_ready' && qrCode ? (
              <div className="relative w-full h-full flex items-center justify-center animate-in zoom-in duration-500 rounded-xl overflow-hidden bg-white p-2">
                <img src={qrCode} alt="QR Code" className="w-full h-full object-contain" />
              </div>
            ) : waStatus === 'connected' ? (
              <div className="flex flex-col items-center text-seryi-green gap-3 animate-in zoom-in">
                <CheckCircle size={40} />
                <span className="text-[10px] font-bold text-center text-emerald-400">Sistema<br/>Sincronizado</span>
              </div>
            ) : (
              <div className="flex flex-col items-center text-slate-600 gap-3">
                <Smartphone size={32} className="opacity-50" />
                <span className="text-[10px] font-semibold text-center uppercase tracking-widest">Esperando<br/>Conexión</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
